import { Component, computed, effect, inject, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AgGridAngular } from 'ag-grid-angular';
import { ClientSideRowModelModule, GridOptions, themeQuartz, type ColDef } from 'ag-grid-community';
import { BrandNameLogoRendererComponent } from '../../../components/brands-dashboard-screen/dashboard-table/custom-table-components/brand-name-logo/brand-name-logo-renderer.component';
import { DashboardService, SelectBrandItem } from '../../../services/dashboard.service';
import { StateService } from '../../../services/state.service';
import { MESSAGING_BRAND_HEADER_WIDTH } from '../types';

@Component({
  selector: 'app-brand-header',
  imports: [AgGridAngular, CommonModule],
  templateUrl: './brand-header.component.html',
  styleUrl: './brand-header.component.scss'
})
export class BrandHeaderComponent {
  private dashboardService = inject(DashboardService);
  protected stateService = inject(StateService);

  @Input() maxBrandColumns: number = 6;

  brands = computed(() => {
    const myBrand = this.dashboardService.myBrand();
    const competitors = this.dashboardService.myCompetitors();
    if (myBrand?.key) {
      return [myBrand, ...competitors].filter(b => b?.key);
    }
    return [];
  });

  rowData: Record<string, any>[] = [{ empty: '' }];
  colDefs: ColDef[] = [{
    field: 'empty',
    headerName: '',
    pinned: 'left',
    flex: 0,
    width: MESSAGING_BRAND_HEADER_WIDTH,
    colId: 'metric-column',
    cellRenderer: () => '',
  }];

  constructor() {
    effect(() => {
      const brands = this.brands();
      if (brands.length > 0) {
        this.initRowData(brands);
        this.initColDefs(brands);
      }
    });
  }

  private initRowData(brands: SelectBrandItem[]): void {
    const row: Record<string, any> = { empty: '' };
    
    for (let i = 0; i < this.maxBrandColumns; i++) {
      const brand = brands[i];
      const fieldKey = `brand_${i}`;
      if (brand?.key) {
        row[fieldKey] = {
          name: brand.label,
          logoPath: brand.icon || 'images/no-logo.png'
        };
      } else {
        row[fieldKey] = undefined;
      }
    }
    
    this.rowData = [row];
  }

  private initColDefs(brands: SelectBrandItem[]): void {
    const brandColDefs: ColDef[] = [];
    
    for (let i = 0; i < this.maxBrandColumns; i++) {
      const brand = brands[i];
      const fieldKey = `brand_${i}`;
      const colDef: ColDef = {
        field: fieldKey,
        headerName: '',
        flex: 1,
        valueGetter: (params: any) => {
          return params.data?.[fieldKey];
        },
        cellRenderer: brand?.key ? BrandNameLogoRendererComponent : undefined,
        cellRendererParams: brand?.key ? {
          customClass: 'brand-header-renderer'
        } : undefined
      };

      if (i === 0) {
        colDef.colId = 'my-brand-column';
      }

      brandColDefs.push(colDef);
    }

    this.colDefs = [
      {
        field: 'empty',
        headerName: '',
        pinned: 'left',
        flex: 0,
        width: MESSAGING_BRAND_HEADER_WIDTH,
        colId: 'metric-column',
        cellRenderer: () => '',
      },
      ...brandColDefs,
    ];
  }

  theme = themeQuartz.withParams({
    spacing: 10,
    rowHoverColor: 'transparent',
    fontSize: '14px',
    fontFamily: 'Poppins',
    cellTextColor: '#717172',
    cellHorizontalPadding: 10,
  });

  modules = [ClientSideRowModelModule];
  gridOptions: GridOptions = {
    suppressMovableColumns: true,
    domLayout: 'autoHeight',
    rowHeight: 64,
    headerHeight: 0,
      defaultColDef: {
        resizable: false,
        autoHeight: false,
        sortable: false,
        wrapText: true,
        flex: 1
      },
  };
}

